import {
  type LayoutChangeEvent,
  Platform,
  useWindowDimensions,
  View,
} from "@-/com.npmjs.react-native";
import { StatusBar } from "@-/com.npmjs.expo-status-bar";
import { ScopedTheme } from "@-/com.npmjs.uniwind";
import { FrostedBackdropTarget } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  runOnJS,
  useAnimatedReaction,
  useDerivedValue,
  useReducedMotion,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import {
  isBootsplashRevealed,
  whenBootsplashRevealed,
} from "@-/dev.improve.haniverse.lib.bootsplash";
import { useAsyncComponent } from "@-/dev.improve.haniverse.lib.use-async-component";
import { useVisualActivity } from "@-/dev.improve.haniverse.lib.visual-activity";
import { useAuthSheet } from "@-/dev.improve.haniverse.routes.nav.app.auth.sheet";
import {
  registerWelcomeGalaxyScene,
  takeWelcomeGalaxyExitSnapshot,
  type WelcomeGalaxyCanvasCache,
  type WelcomeGalaxySceneLaunch,
} from "@-/dev.improve.haniverse.routes.nav.app.auth.welcome-galaxy-entry";
import { createElement, useCallback, useEffect, useMemo, useState } from "react";
import { loadWelcomeGalaxyCanvas } from "#load-welcome-galaxy-canvas";
import { useWelcomeGalaxyClock } from "#use-welcome-galaxy-clock";
import { useWelcomeGalaxySpinGesture } from "#use-welcome-galaxy-spin-gesture";
import { WelcomeGalaxyOverlay } from "#welcome-galaxy-overlay";
import { WelcomeHaniWorld } from "#welcome-hani-world";
import { isWelcomeGalaxySkiaDisabled } from "#is-welcome-galaxy-skia-disabled";
import {
  WELCOME_GALAXY_LAUNCH_DURATION,
  welcomeGalaxyLaunchSkyProgress,
} from "#welcome-galaxy-launch";
import { WelcomeGalaxyLaunchSky } from "#welcome-galaxy-launch-sky";
import { welcomeGalaxyFormationOpacity } from "#welcome-hani-world-layout";
import { welcomeGalaxyPalette, type WelcomeGalaxyAppearance } from "#welcome-galaxy-palette";
import { WelcomeGalaxyStandIn, WelcomeGalaxyStaticFallback } from "#welcome-galaxy-static-fallback";
import {
  isWelcomeGalaxySplitLayout,
  welcomeGalaxyEntryUiOpacity,
  WELCOME_GALAXY_SETTLED_T,
  type WelcomeGalaxyViewport,
} from "#welcome-galaxy-timeline";

/**
 * How long the stand-in (`WelcomeGalaxyStandIn`) waits for the Skia scene's GPU pipelines to be
 * compiled ahead of time (`onPipelinesReady`) before the scene joins anyway and compiles whatever is
 * left as it goes. Compiling blocks the UI thread, so it is done while the scene stands still. A
 * cold shader cache took 4.6–5.1 s on the iOS simulator (dev build); a warm one about 1 s. Only
 * what is drawn waits for this: the headline and buttons run on the page's clock regardless.
 */
const PIPELINE_WARM_UP_DEADLINE_MS = 6000;

/**
 * The server-rendered web page has no window to size itself by, so web keeps this size until the
 * page has measured itself: its first render then hydrates the very tree the server rendered.
 */
const UNMEASURED_WEB_SIZE = { height: 0, width: 0 };

/**
 * Whether this mount is the first thing the app shows after a cold launch, with the native splash
 * still up: then the page opens on a copy of the splash and launches out of it. Web has no native
 * splash, and reduced motion and builds without Skia skip the show.
 */
function shouldLaunchFromSplash({
  isSkiaDisabled,
  reduceMotion,
}: {
  isSkiaDisabled: boolean;
  reduceMotion: boolean;
}): boolean {
  return Platform.OS !== "web" && !isSkiaDisabled && !reduceMotion && !isBootsplashRevealed();
}

/**
 * Hani grows out of the native splash and lands on an expanding Earth while the tilted galaxy
 * gains opacity in place. The full-width title rests below Hani, above the tagline and actions.
 * Everything follows the page clock, including the SVG stand-in while Skia warms up. Reduced
 * motion starts at the final composition. Sign-out retains its snapshot blur and galaxy exit;
 * sign-in reuses the registered live galaxy for the entry into the app.
 */
export function AuthWelcomeGalaxy({ appearance }: { appearance: WelcomeGalaxyAppearance }) {
  const palette = welcomeGalaxyPalette(appearance);
  const [isSkiaDisabled] = useState(isWelcomeGalaxySkiaDisabled);
  const reduceMotion = useReducedMotion();
  const [launchFromSplash] = useState(() =>
    shouldLaunchFromSplash({ isSkiaDisabled, reduceMotion }),
  );
  // What the page opens on and launches out of: the splash on a cold launch, or after a sign-out
  // the picture of the app it took (`captureWelcomeGalaxyExit`), which this page now owns. The
  // picture is left to the garbage collector: the canvas keeps drawing it (at nothing) after the
  // launch, so disposing it any earlier than the page could draw a disposed image.
  const [launch] = useState<WelcomeGalaxySceneLaunch>(() => {
    if (launchFromSplash) {
      return { kind: "splash" };
    }
    const snapshot = takeWelcomeGalaxyExitSnapshot();
    if (snapshot === null) {
      return null;
    }
    if (isSkiaDisabled || reduceMotion) {
      snapshot.dispose();
      return null;
    }
    return { kind: "snapshot", snapshot };
  });
  const [isWaitingForSplash, setIsWaitingForSplash] = useState(launchFromSplash);
  const [arePipelinesReady, setArePipelinesReady] = useState(false);
  const entryTime = useSharedValue(0);
  const Canvas = useAsyncComponent(loadWelcomeGalaxyCanvas);
  const [canvasCache] = useState<WelcomeGalaxyCanvasCache>(() => ({ current: null }));
  const isVisuallyActive = useVisualActivity();
  const { fastForward, introTime, isSceneLive, simTime } = useWelcomeGalaxyClock({
    entryTime,
    isRevealed: !isWaitingForSplash,
    // Readiness only decides what is drawn: the scene's canvas is up and has compiled its
    // pipelines. The page's clock, and with it the headline and buttons, never waits for it.
    isSceneReady: Canvas !== null && isVisuallyActive && arePipelinesReady,
    startSettled: isSkiaDisabled,
    startTime: launch === null ? 0 : -WELCOME_GALAXY_LAUNCH_DURATION,
  });
  // The cold welcome forms in place. Keep the flight clock only for sign-out's snapshot exit.
  const sceneLaunch = launch?.kind === "snapshot" ? launch : null;
  const formsInPlace = sceneLaunch === null;
  const galaxyTime = useDerivedValue(() =>
    formsInPlace ? WELCOME_GALAXY_SETTLED_T + Math.max(0, simTime.get()) : simTime.get(),
  );
  const formationStyle = useAnimatedStyle(() => ({
    opacity: formsInPlace ? welcomeGalaxyFormationOpacity(introTime.get()) : 1,
  }));
  const windowSize = useWindowDimensions();
  const [measuredSize, setMeasuredSize] = useState<WelcomeGalaxyViewport | null>(null);
  // The window stands in until the first layout, so the buttons render on the very first frame.
  const { height, width } =
    measuredSize ?? (Platform.OS === "web" ? UNMEASURED_WEB_SIZE : windowSize);
  const viewport = useMemo<WelcomeGalaxyViewport>(
    () => ({
      // Leave a little more sky around the galaxy in both portrait and the landscape panel.
      galaxyScale:
        Platform.OS !== "web" && isWelcomeGalaxySplitLayout({ height, width }) ? 0.9 : 1.5,
      height,
      stageWidth:
        Platform.OS !== "web" && isWelcomeGalaxySplitLayout({ height, width }) ? width / 2 : width,
      width,
    }),
    [height, width],
  );
  const [isSkyLight, setIsSkyLight] = useState(launchFromSplash);
  const overlayAppearance: WelcomeGalaxyAppearance = isSkyLight ? "light" : appearance;
  // The stand-in drawn in the scene's place goes once it has crossfaded to the live scene.
  const [isStandInRetired, setIsStandInRetired] = useState(false);
  const retireStandIn = useCallback(() => setIsStandInRetired(true), []);

  const { gesture, interactionProgress, spinOffset } = useWelcomeGalaxySpinGesture({
    onTouch: fastForward,
    viewport,
  });
  // The galaxy regains its full strength while it is held, and while the auth sheet is up over
  // it. The wordmark only fades on a hold (`interactionProgress`).
  const { presentation: sheetPresentation } = useAuthSheet();
  const galaxyFocusProgress = useDerivedValue(() =>
    Math.max(interactionProgress.get(), sheetPresentation.get()),
  );

  const galaxyDimStyle = useAnimatedStyle(() => ({
    opacity:
      0.48 *
      (1 - galaxyFocusProgress.get()) *
      welcomeGalaxyFormationOpacity(introTime.get()) *
      welcomeGalaxyEntryUiOpacity(entryTime.get()),
  }));

  // The page is revealed once the native splash is down. `whenBootsplashRevealed` fails open after
  // its deadline, so a splash that never reports hiding still lets the launch play.
  useEffect(() => {
    if (!launchFromSplash) {
      return;
    }
    let active = true;
    void whenBootsplashRevealed().then(() => {
      if (active) {
        setIsWaitingForSplash(false);
      }
    });
    return () => {
      active = false;
    };
  }, [launchFromSplash]);

  // The scene stands still while its canvas compiles what it will draw (see `onPipelinesReady`),
  // but never longer than `PIPELINE_WARM_UP_DEADLINE_MS`: the warm-up may never settle. A canvas
  // that has not mounted by then joins as soon as it does; until then the stand-in stays.
  const onPipelinesReady = useCallback(() => setArePipelinesReady(true), []);
  useEffect(() => {
    if (arePipelinesReady) {
      return;
    }
    const deadline = setTimeout(onPipelinesReady, PIPELINE_WARM_UP_DEADLINE_MS);
    return () => {
      clearTimeout(deadline);
    };
  }, [arePipelinesReady, onPipelinesReady]);

  useEffect(
    () =>
      registerWelcomeGalaxyScene({
        appearance,
        canvasCache,
        entryTime,
        launch: sceneLaunch,
        simTime: galaxyTime,
        spinOffset,
        viewport,
      }),
    [appearance, canvasCache, entryTime, sceneLaunch, galaxyTime, spinOffset, viewport],
  );

  // Dark status bar text on the splash's white sky, light once it has darkened to space (the
  // light galaxy's sky stays white; a launch out of the app starts in the galaxy's own sky).
  const launchKind = launch?.kind ?? "splash";
  useAnimatedReaction(
    () => welcomeGalaxyLaunchSkyProgress(introTime.get(), launchKind) < 0.5,
    (isLight, wasLight) => {
      if (isLight !== wasLight) {
        runOnJS(setIsSkyLight)(isLight);
      }
    },
  );

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const { height: layoutHeight, width: layoutWidth } = event.nativeEvent.layout;
    setMeasuredSize((previous) =>
      previous?.height === layoutHeight && previous.width === layoutWidth
        ? previous
        : { height: layoutHeight, width: layoutWidth },
    );
  }, []);

  return (
    <View className="flex-1" onLayout={onLayout} testID="auth-welcome-galaxy">
      {isVisuallyActive ? (
        <StatusBar style={isSkyLight || appearance === "light" ? "dark" : "light"} />
      ) : null}
      <FrostedBackdropTarget className="absolute inset-0">
        <WelcomeGalaxyLaunchSky
          introTime={introTime}
          kind={launch?.kind ?? "splash"}
          palette={palette}
        />
        <GestureDetector gesture={gesture}>
          <Animated.View
            className="absolute inset-0"
            collapsable={false}
            style={formationStyle}
            testID="auth-welcome-galaxy-canvas"
          >
            {isSkiaDisabled ? (
              <WelcomeGalaxyStaticFallback appearance={appearance} viewport={viewport} />
            ) : (
              <>
                {Canvas === null || !isVisuallyActive
                  ? null
                  : createElement(Canvas, {
                      appearance,
                      cache: canvasCache,
                      entryTime,
                      launch: sceneLaunch,
                      onPipelinesReady,
                      role: "welcome",
                      simTime: galaxyTime,
                      spinOffset,
                      viewport,
                    })}
                {isStandInRetired ? null : (
                  <WelcomeGalaxyStandIn
                    appearance={appearance}
                    introTime={introTime}
                    isSceneLive={isSceneLive}
                    launchKind={launchKind}
                    onRetired={retireStandIn}
                    viewport={viewport}
                  />
                )}
              </>
            )}
          </Animated.View>
        </GestureDetector>
        <Animated.View
          className={`absolute inset-0 ${palette.pageClassName}`}
          pointerEvents="none"
          style={galaxyDimStyle}
        />
        {viewport.width > 0 && viewport.height > 0 ? (
          <WelcomeHaniWorld
            appearance={appearance}
            entryTime={entryTime}
            introTime={introTime}
            launchFromSplash={launchFromSplash}
            viewport={viewport}
          />
        ) : null}
      </FrostedBackdropTarget>
      {/* The tagline and buttons are there from the first frame, over the splash's white sky too:
          the dark galaxy's are in the light theme's ink until its sky darkens past its middle
          tones, like its characters and the status bar. The scoped theme carries that to their
          theme colours (`text-foreground`), which otherwise follow the app's. */}
      <ScopedTheme theme={overlayAppearance}>
        <WelcomeGalaxyOverlay
          appearance={overlayAppearance}
          entryTime={entryTime}
          focusProgress={interactionProgress}
          introTime={introTime}
          viewport={viewport}
        />
      </ScopedTheme>
    </View>
  );
}
